import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { ImageIntake } from '@/components/shared/ImageIntake'
import { SizeInfo } from './SizeInfo'
import { useToast } from '@/hooks/use-toast'
import { shapesApi, SHAPE_ACCEPT, graphicFilenameFor, readImageDimensions, type ShapeItem } from '@/features/shapes/api/shapes'
import {
    defaultSize, drawnBox, isVectorFilename, recommendedPixels, sharpness, sharpUpToZoom,
    type PixelSize,
} from '@/features/shapes/lib/graphic-resolution'
import { toAssetBaseName } from '@/lib/assetFilename'

/**
 * Adding a graphic to the library — the one form, used from both places it can be done.
 *
 * The library screen and the classification assignment dialog both upload artwork, and they were
 * asking for different things: the library wanted a category, a name, tags and a description with
 * a preview and the filename it would be saved under; the dialog asked for a name and tags. Two
 * forms for one operation means the graphic you get depends on where you happened to be standing,
 * and the one uploaded from the dialog arrived with no description — which is the NAME everything
 * else displays.
 *
 * `fixedCategory` is the only difference that survives, and it is a real one: a classification
 * already knows its category, so asking again is asking someone to repeat themselves and to get
 * it wrong, which files the graphic where that dialog will never offer it.
 */
export function GraphicUploadForm({
    fixedCategory,
    categories = [],
    initialCategory,
    onUploaded,
    onCancel,
    submitLabel,
}: {
    /** Set by a caller that already knows the category. Hides the selector. */
    fixedCategory?: string
    /** Offered when the caller does not. Ignored when fixedCategory is set. */
    categories?: string[]
    /** Pre-selection for the selector — the library pre-sets the category being browsed. */
    initialCategory?: string
    onUploaded: (shape: ShapeItem) => void
    onCancel?: () => void
    submitLabel?: string
}) {
    const { t, i18n } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()

    const [file, setFile] = useState<File | null>(null)
    const [preview, setPreview] = useState<string | null>(null)
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [tags, setTags] = useState('')
    const [category, setCategory] = useState(fixedCategory ?? initialCategory ?? '')
    const [uploading, setUploading] = useState(false)
    // The name the library already holds, when a save comes back 409. Its presence opens the
    // confirmation — an in-app dialog, not window.confirm, which cannot be styled, cannot be
    // translated by us, and in a bulk loop stacks one native prompt per file.
    const [clash, setClash] = useState<string | null>(null)

    // An object URL is a live handle on a file; leaking one per staged image keeps the bytes
    // alive for as long as the tab is open.
    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview) }, [preview])

    // The staged file's pixels, read the way the upload records them. A newer file supersedes a
    // read still in flight, so a slow decode cannot label the wrong image.
    const [pixels, setPixels] = useState<PixelSize | null>(null)
    const staging = useRef(0)

    const stage = (f: File) => {
        setFile(f)
        setPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(f) })
        const token = ++staging.current
        setPixels(null)
        readImageDimensions(f).then((d) => { if (token === staging.current) setPixels(d) })
        // A pasted image has no meaningful name of its own; leave it for the user.
        const base = f.name && !/^image\.(png|jpe?g)$/i.test(f.name)
            ? f.name.replace(/\.[^.]+$/, '')
            : ''
        setName((prev) => prev || base)
    }

    // Builds AS YOU TYPE, not once a file exists. The name is transformed on the way to a
    // filename ("Cisco Switch" -> "cisco-switch"), and that is worth seeing while typing; showing
    // nothing until an image was staged made the field look broken. Before a file arrives there
    // is no extension to promise, so it shows the base name and gains the suffix later.
    const savedAs = !(name.trim() || file)
        ? ''
        : file
            ? graphicFilenameFor(file, name)
            : toAssetBaseName(name, 'shape')

    // SIZE and RESOLUTION, kept apart (lib/graphic-resolution.ts). The app sets the size — 2 cm high,
    // for a new graphic and a replacement alike — and the file's pixels then only decide how far the
    // canvas can zoom before it blurs. A vector never blurs.
    const vector = !!file && (file.type === 'image/svg+xml' || isVectorFilename(file.name))
    const size = file ? defaultSize(pixels) : null
    const box = size ? drawnBox(size) : null
    const zoom = pixels && size && !vector ? sharpUpToZoom(pixels, size) : null
    const needed = size ? recommendedPixels(size) : null
    const formatZoom = (z: number) => (Math.floor(z * 10) / 10).toLocaleString(i18n.language, { maximumFractionDigits: 1 })

    const send = async (overwrite: boolean) => {
        if (!file) return
        const named = new File([file], graphicFilenameFor(file, name), { type: file.type || 'image/png' })
        setUploading(true)
        try {
            const created = await shapesApi.upload(
                named, description || undefined, tags || undefined, overwrite, category,
            )
            queryClient.invalidateQueries({ queryKey: ['shapes'] })
            toast({
                title: overwrite
                    ? t('shapes.replacedTitle', 'Graphic image replaced')
                    : t('shapes.addedTitle', 'Graphic added'),
                description: t('shapes.addedDescription', '{{name}} added to the library.', { name: named.name }),
            })
            setClash(null)
            onUploaded(created)
        } catch (err) {
            const e = err as { response?: { status?: number; data?: { message?: string } }; message?: string }
            // 409 means the filename is taken. The bytes are NOT at that key yet — the presign
            // and the PUT happen inside upload() before the metadata call — so declining here
            // leaves the library exactly as it was.
            if (!overwrite && e?.response?.status === 409) {
                setClash(named.name)
                return
            }
            setClash(null)
            toast({
                variant: 'destructive',
                title: t('shapes.uploadFailed', 'Upload failed'),
                description: e?.response?.data?.message || e?.message || t('shapes.tryAgain', 'Please try again.'),
            })
        } finally {
            setUploading(false)
        }
    }

    return (
        <>
            <div className="space-y-4">
                <ImageIntake onFile={stage} accept={SHAPE_ACCEPT} disabled={uploading} />

                {preview && (
                    <div className="flex items-center gap-3 rounded-md border p-3">
                        <img src={preview} alt="" className="h-16 w-16 object-contain" />
                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground">
                                {t('shapes.previewNote', 'Resized when saved to what the canvas needs: sharp at every zoom, quick to load.')}
                            </p>
                            {pixels && !vector && (
                                <p className="text-xs tabular-nums text-muted-foreground" data-testid="graphic-pixel-size">
                                    {pixels.width} × {pixels.height} px
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {vector && file ? (
                    <p className="text-xs text-muted-foreground">{t('shapes.vectorSharp', 'Vector: sharp at every zoom.')}</p>
                ) : zoom !== null && box && needed && (sharpness(zoom) === 'blurs' ? (
                    /* A div rather than a p: the tooltip renders its content inline here, and a
                       paragraph cannot legally contain it. */
                    <div
                        role="alert"
                        className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400"
                        data-testid="graphic-sharpness"
                    >
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                        {t('shapes.cardBlursPast', 'Blurs past {{zoom}}× zoom', { zoom: formatZoom(zoom) })}
                        <SizeInfo box={box} needed={needed} zoom={zoom} />
                    </div>
                ) : (
                    <p className="text-xs text-emerald-700 dark:text-emerald-400" data-testid="graphic-sharpness">
                        {sharpness(zoom) === 'every'
                            ? t('shapes.sharpAtEveryZoom', 'Sharp at every zoom.')
                            : t('shapes.sharpToZoom', 'Sharp to {{zoom}}× zoom.', { zoom: formatZoom(zoom) })}
                    </p>
                ))}

                {fixedCategory ? (
                    <p className="text-[11px] text-muted-foreground">
                        {t('classifications.graphicsUploadHelp',
                            'Added to the {{category}} category, because that is this classification’s category. Tags are what decide when this graphic is chosen over the default.',
                            { category: fixedCategory })}
                    </p>
                ) : (
                    <div className="space-y-2">
                        <Label>{t('shapes.categoryLabel', 'Category')}</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger>
                                <SelectValue placeholder={t('shapes.categoryPlaceholder', 'Which category this graphic belongs in')} />
                            </SelectTrigger>
                            <SelectContent>
                                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground">
                            {t('shapes.categoryHelp', 'A category, not a classification — one network graphic serves every network classification. Use tags below for the specifics (cisco, switch, vm, windows).')}
                        </p>
                    </div>
                )}

                <div className="space-y-2">
                    <Label htmlFor="graphic-name">{t('shapes.nameLabel', 'Name')}</Label>
                    <Input
                        id="graphic-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('shapes.namePlaceholder', 'e.g. network-switch')}
                    />
                    {savedAs && (
                        <p className="text-[11px] text-muted-foreground">
                            {t('shapes.savedAs', 'Saved as')} <span className="font-mono">{savedAs}</span>
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label htmlFor="graphic-tags">{t('shapes.tags', 'Tags')}</Label>
                    <Input
                        id="graphic-tags"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        placeholder={t('shapes.tagsPlaceholder2', 'e.g. cisco, switch, network')}
                    />
                    <p className="text-[11px] text-muted-foreground">
                        {t('shapes.tagsHelp', 'Tags carry the specifics within a category — cisco, switch, vm, windows. The category picks the section; these pick the thing.')}
                    </p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="graphic-description">{t('shapes.description', 'Description')}</Label>
                    <Input
                        id="graphic-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={t('shapes.descriptionPlaceholder', 'What this graphic represents')}
                    />
                </div>

                <div className="flex justify-end gap-2">
                    {onCancel && (
                        <Button variant="outline" onClick={onCancel} disabled={uploading}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                    )}
                    <Button
                        onClick={() => send(false)}
                        disabled={!file || !category || uploading}
                        title={
                            !file
                                ? t('shapes.needImage', 'Choose a file or paste an image first')
                                : !category
                                    ? t('shapes.needCategory', 'Pick a category first')
                                    : undefined
                        }
                    >
                        {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                        {submitLabel || t('shapes.addAction', 'Add graphic')}
                    </Button>
                </div>
            </div>

            <Dialog open={!!clash} onOpenChange={(open) => { if (!open && !uploading) setClash(null) }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t('shapes.overwriteTitle', 'That name is already taken')}</DialogTitle>
                        <DialogDescription>
                            {t('shapes.overwriteConfirm', '“{{name}}” already exists in the library. Replace it?', { name: clash || '' })}
                        </DialogDescription>
                    </DialogHeader>
                    <p className="text-xs text-muted-foreground">
                        {t('shapes.overwriteWarning',
                            'Replacing changes the image everywhere this graphic is already used, on every classification it is assigned to.')}
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setClash(null)} disabled={uploading}>
                            {t('common.cancel', 'Cancel')}
                        </Button>
                        <Button variant="destructive" onClick={() => send(true)} disabled={uploading}>
                            {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {t('shapes.overwriteAction', 'Replace it')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
